import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useUpdateEmailTemplateMutation } from '@/redux/email-templates/emailTemplatesApi';
import type { CatalogueEntry } from '@/redux/email-templates/emailTemplatesApi';
import type { RootState } from '@/redux/common/store';
import { Modal } from '@/components/UI/Modal';
import { Button } from '@/components/UI/Button';
import { Badge } from '@/components/UI/Badge';
import { Tabs } from '@/components/UI/Tabs';
import { fieldClass } from '@/components/UI/fieldStyles';

interface Props {
  entry: CatalogueEntry;
  onClose: () => void;
}

const TemplateEditorModal: React.FC<Props> = ({ entry, onClose }) => {
  const { t } = useTranslation();
  const roles = useSelector((state: RootState) => state.sessionState.roles);
  const isPlatformAdmin = roles.includes('platform_admin') || roles.includes('platform_support');

  const effective = entry.effective_value as { subject?: string; content?: string };
  const [subject, setSubject] = useState(effective.subject ?? '');
  const [content, setContent] = useState(effective.content ?? '');
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit');
  const [error, setError] = useState('');

  const [updateConfig, { isLoading }] = useUpdateEmailTemplateMutation();

  useEffect(() => {
    setSubject(effective.subject ?? '');
    setContent(effective.content ?? '');
    setError('');
  }, [entry.key]);

  const isBase = entry.key === 'email_template.base';
  const hasTenantOverride = Object.keys(entry.tenant_value ?? {}).length > 0;
  const variables: string[] = entry.variables ?? [];

  const handleSave = async () => {
    setError('');
    const value: Record<string, string> = { content };
    if (!isBase) value.subject = subject;
    try {
      await updateConfig({ key: entry.key, value }).unwrap();
      onClose();
    } catch {
      setError(t('common.failedToSave'));
    }
  };

  // The preview is a sandboxed iframe document, so it cannot see Tailwind classes or CSS variables:
  // resolve the accent tokens from the app root and fall back to system highlight colours.
  const rootStyle = getComputedStyle(document.documentElement);
  const tokenColor = (name: string, fallback: string) => {
    const v = rootStyle.getPropertyValue(name).trim();
    return v ? `rgb(${v})` : fallback;
  };
  const chipBg = tokenColor('--ui-accent-soft', 'Mark');
  const chipFg = tokenColor('--ui-accent-text', 'MarkText');
  const previewHtml = content.replace(
    /\{\{[\w]+\}\}/g,
    (m) =>
      `<span style="background:${chipBg};color:${chipFg};padding:0 3px;border-radius:3px;font-weight:600">${m}</span>`,
  );

  return (
    <Modal
      title={entry.name ?? entry.key}
      onClose={onClose}
      maxWidthClassName="max-w-4xl"
      bodyClassName="p-0 space-y-0"
      closeOnBackdrop={false}
      footer={
        <>
          {error && <p className="mr-auto text-danger-text text-sm">{error}</p>}
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSave} loading={isLoading} disabled={isLoading}>
            {`Save ${isPlatformAdmin ? 'System Default' : 'Tenant Override'}`}
          </Button>
        </>
      }
    >
      {/* Badges + key + variables */}
      <div className="px-6 pt-4">
        <div className="flex items-center gap-2 mb-1">
          <Badge variant="accent">{isPlatformAdmin ? 'System layer' : 'Tenant override'}</Badge>
          {hasTenantOverride && isPlatformAdmin && <Badge variant="warning">Tenant override active</Badge>}
        </div>
        <p className="text-xs text-ui-text2">
          <code className="rounded-chip bg-ui-subtle px-1.5 py-0.5 text-xs">{entry.key}</code>
          {variables.length > 0 && (
            <span className="ml-3">
              Variables:{' '}
              {variables.map((v) => (
                <code key={v} className="mr-1 rounded-chip bg-accent-soft px-1.5 py-0.5 text-xs text-accent-text">
                  {`{{${v}}}`}
                </code>
              ))}
            </span>
          )}
        </p>
      </div>

      {/* Subject */}
      {!isBase && (
        <div className="px-6 pt-4">
          <label className="block text-sm font-medium text-ui-text mb-1">Subject Line</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className={fieldClass()}
          />
        </div>
      )}

      {/* Tabs */}
      <div className="mt-4 px-6">
        <Tabs
          label="Template view"
          value={activeTab}
          onChange={(id) => setActiveTab(id as 'edit' | 'preview')}
          tabs={[
            { id: 'edit', label: 'Edit' },
            { id: 'preview', label: 'Preview' },
          ]}
        />
      </div>

      {/* Editor / Preview */}
      <div className="px-6 py-4">
        {activeTab === 'edit' ? (
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className={`${fieldClass({ shape: 'box' })} h-[45dvh] min-h-[300px] font-mono resize-none`}
            spellCheck={false}
          />
        ) : (
          <iframe
            srcDoc={previewHtml}
            sandbox="allow-same-origin"
            title="Email preview"
            className="w-full h-[45dvh] min-h-[300px] border border-ui-border bg-ui-raised rounded-control"
          />
        )}
      </div>
    </Modal>
  );
};

export default TemplateEditorModal;
